import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton "Mettre en favori" reutilisable pour chat agents et Tables Rondes.
 *
 * Props :
 *  - kind: "chat" | "roundtable"
 *  - targetId: conversation_id (chat) ou roundtable_id (TR) - REQUIRED
 *  - agentType: pour les chats (ex: "fiscalite"), pour TR -> "roundtable"
 *  - summary: optionnel, court resume affiche dans la liste favoris
 *  - disabled: desactive le bouton (ex: pas encore de messages)
 *  - className: classes additionnelles pour le bouton
 *  - variant: "icon" (petit cercle) ou "full" (avec libelle)
 */
export default function FavoriteButton({
  kind = "chat",
  targetId,
  agentType,
  summary = "",
  disabled = false,
  className = "",
  variant = "icon",
  label = "Mettre en favori",
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleOpen = async () => {
    if (!targetId) {
      toast.error("Discussion non sauvegardee — envoie au moins un message.");
      return;
    }
    setOpen(true);
    setTitle("");
    setLoadingSuggest(true);
    try {
      const token = localStorage.getItem("odys_token");
      const res = await fetch(`${API}/api/favorites/suggest-title`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ kind, target_id: targetId }),
      });
      if (res.ok) {
        const data = await res.json();
        setTitle(data.title || "");
      } else {
        setTitle(kind === "roundtable" ? "Reunion d'equipage" : "Discussion Odys'IA");
      }
    } catch (err) {
      setTitle(kind === "roundtable" ? "Reunion d'equipage" : "Discussion Odys'IA");
    } finally {
      setLoadingSuggest(false);
    }
  };

  const handleSave = async () => {
    const cleaned = title.trim();
    if (!cleaned) {
      toast.error("Donne un titre a ce favori.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("odys_token");
      const res = await fetch(`${API}/api/favorites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversation_id: targetId,
          agent_type: agentType || (kind === "roundtable" ? "roundtable" : "ia"),
          title: cleaned,
          summary: summary || "",
          kind,
        }),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success("Ajoute aux favoris");
      setOpen(false);
    } catch (err) {
      toast.error("Impossible d'ajouter aux favoris.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {variant === "icon" ? (
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          data-testid="favorite-btn"
          aria-label="Mettre en favori"
          title="Mettre en favori"
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-[color:var(--line)] bg-[color:var(--paper)] text-[color:var(--ink)] hover:bg-[color:var(--gold-bg)] hover:border-[color:var(--gold)] transition disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
        >
          <Star className="w-4 h-4" />
        </button>
      ) : (
        <Button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          variant="outline"
          data-testid="favorite-btn"
          className={`tr-secondary-btn ${className}`}
        >
          <Star className="w-4 h-4 mr-2" />
          {label}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="favorite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#B5894A]" />
              Mettre cette discussion en favori
            </DialogTitle>
            <DialogDescription>
              Donne-lui un titre pour la retrouver facilement dans tes favoris.
              L'IA en a propose un — modifie-le librement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Titre
            </label>
            {loadingSuggest ? (
              <div className="flex items-center gap-2 h-10 px-3 border rounded-md text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Generation du titre...
              </div>
            ) : (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Achat T2 Lyon en LMNP"
                maxLength={80}
                autoFocus
                data-testid="favorite-title-input"
              />
            )}
            <p className="text-xs text-muted-foreground text-right">{title.length}/80</p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="favorite-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || loadingSuggest || !title.trim()}
              data-testid="favorite-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
